import { ID } from 'types/utility'
import { TripShort } from 'types/trips'
import { TransactionShort } from 'types/transactions'

export const USER_ENDPOINTS = {
  BASE: 'users',
  CURRENT_USER: 'users/me',
  CURRENT_USER_TOTALS: 'users/me/totals',
  LOGIN: 'login',
  SESSION: 'session',
  SAML_LOGIN: 'saml_login',
  AUTH_ACTION: 'sessions',
  OAUTH_LOGIN: 'oauth_sessions',
  SSO: 'sso',
  COMPLIANCE_HISTORY: 'users/compliances',
  REQUEST_PASSWORD_RESETS: 'password_resets',
  RESET_PASSWORD: (userId: ID) => {
    return `password_resets/${userId}`
  },
  USER_COMPLIANCE_HISTORY: (userId: ID) => {
    return `users/${userId}/compliances`
  },
  DISCONNECT_OAUTH: (provider: string, passphrase: string) => {
    return `oauth_sessions?provider=${provider}&passphrase=${passphrase}`
  },
  USER_PROGRAM_HISTORY_LIST: (userId: ID) => {
    return `users/${userId}/program_history`
  },
  USER_PROGRAM_HISTORY: (userId: ID, historyId: ID) => {
    return `users/${userId}/program_history/${historyId}`
  },
  MANAGE_SUBSCRIPTIONS: (tokenId: ID) => {
    return `manage_subscriptions/${tokenId}`
  },
  SLT: (tokenId: ID) => {
    return `users/${tokenId}/single_login_token`
  },
  INTERCOM_TOKEN: 'users/intercom_token'
}

export const MOTUS_ENDPOINTS = {
  LOGIN: 'login',
  SETTINGS: 'login/settings',
  USER: 'users/me',
  REQUEST_PASSWORD_RESETS: 'users/password_resets',
  ROADSMART_VIDEOS: 'roadsmart/videos',
  ROADSMART_VIDEO_STARTED: 'roadsmart/videos/started',
  ROADSMART_VIDEO_COMPLETE: 'roadsmart/videos/complete'
}

export const MEMBER_ENDPOINTS = {
  MEMBERS: (teamId: ID) => {
    return `teams/${teamId}/members`
  },
  MEMBER: (teamId: ID, memberId: ID) => {
    return `teams/${teamId}/members/${memberId}`
  },
  GET_MEMBER_TRANSACTIONS: (teamId: ID, memberId: ID) => {
    return `/teams/${teamId}/members/${memberId}/transactions`
  },
  GET_MEMBER_TRIPS: (teamId: ID, memberId: ID) => {
    return `/teams/${teamId}/members/${memberId}/trips`
  },
  MEMBERS_BATCH_UPDATE: (teamId: ID) => {
    return `teams/${teamId}/batch_members`
  },
  MEMBERS_BATCH_INVITE: (teamId: ID) => {
    return `teams/${teamId}/batch_members/invite`
  },
  MEMBERS_BULK_UPDATE: (teamId: ID) => {
    return `/teams/${teamId}/batch_members/bulk`
  },
  MEMBERS_BULK_UPDATE_TEMPLATE: (teamId: ID) => {
    return `/teams/${teamId}/batch_members/bulk/new`
  },
  MEMBERS_ONBOARDING_STATS: (teamId: ID) => {
    return `teams/${teamId}/members/onboarding_totals`
  }
}

export const MISC_ENDPOINTS = {
  /** Roles */
  ROLES: 'roles',
  /** Reviews */
  REVIEWS: 'reviews',
  REVIEWS_G2: 'reviews/g2',
  REVIEWS_G2_MARK_DONE: 'reviews/g2/mark_as_done',
  REVIEWS_G2_NOT_SHOW: 'reviews/g2/not_show_again',
  /** Bulk imports */
  BULK_IMPORTS: 'bulk_imports',
  /** Text messages */
  SEND_TEXT: 'text_messages',
  /** Jobs */
  JOB_STATUS: (jobId: ID) => {
    return `jobs/${jobId}`
  },
  /** Vehicles */
  VEHICLES: 'vehicles',
  VEHICLE: (vehicleId: ID) => {
    return `vehicles/${vehicleId}`
  },
  /** Institution user */
  INSTITUTION_USER_CREATE_LINK_TOKEN: 'institution_users/create_link_token',
  INSTITUTION_USERS: 'institution_users',
  INSTITUTION_USERS_SYNC: 'institution_users/sync',
  INSTITUTION_TOKEN: (tokenId: ID) => {
    return `institution_users/${tokenId}/public_token`
  },
  INSTITUTION_ACCOUNT: (tokenId: ID) => {
    return `institution_accounts/${tokenId}`
  },
  INSTITUTION_USER: (tokenId: ID) => {
    return `institution_users/${tokenId}`
  },
  INSTITUTION_USER_PUBLIC_TOKEN: (tokenId: ID) => {
    return `institution_accounts/${tokenId}/public_token`
  },
  RECONNECT_INSTITUTION: (tokenId: ID) => {
    return `institution_users/${tokenId}/unexpire_plaid_login`
  },
  /** Favorite transactions */
  FAVORITE_TRANSACTIONS: 'favorite_transactions',
  FAVORITE_TRANSACTION: (transactionId: ID) => {
    return `favorite_transactions/${transactionId}`
  },
  /** Accountant group */
  ACCOUNTANT_GROUPS: 'accountant_groups/current',
  ACCOUNTANT_CLIENT_FIRST: 'accountant_groups/first',
  ACCOUNTANT_CLIENT: (clientId: ID) => {
    return `accountant_groups/clients/${clientId}`
  },
  EXPORT_ACCOUNTANT_CLIENT: (clientId: ID) => {
    return `accountant_groups/clients/${clientId}/export_data`
  }
}

export const PROGRAMS_ENDPOINTS = {
  PROGRAMS: (teamId: ID) => {
    return `teams/${teamId}/programs`
  },
  PROGRAM: (teamId: ID, programId: ID) => {
    return `teams/${teamId}/programs/${programId}`
  },
  PROGRAM_PENDING_CHANGES: (teamId: ID, programId: ID) => {
    return `teams/${teamId}/programs/${programId}/pending_change`
  },
  PROGRAM_RATES: (teamId: ID, programId: ID) => {
    return `teams/${teamId}/programs/${programId}/rates`
  },
  PROGRAM_RATE: (teamId: ID, programId: ID, rateId: ID) => {
    return `teams/${teamId}/programs/${programId}/rates/${rateId}`
  },
  PROGRAM_PURPOSES: (teamId: ID, programId: ID) => {
    return `teams/${teamId}/programs/${programId}/purposes`
  },
  PROGRAM_PURPOSE: (teamId: ID, programId: ID, purposeId: ID) => {
    return `teams/${teamId}/programs/${programId}/purposes/${purposeId}`
  }
}

export const TEAMS_ENDPOINTS = {
  BASE: 'teams',
  TEAM: (teamId: ID) => {
    return `teams/${teamId}`
  },
  TEAM_TOTALS: (teamId: ID) => {
    return `teams/${teamId}/grand_totals`
  },
  TEAM_MEMBER_SUMMARY: (teamId: ID) => {
    return `teams/${teamId}/summary`
  }
}

export const INVITES_ENDPOINTS = {
  BASE: 'invites',
  CREATE_MULTIPLE_INVITES: 'invites/create_multiple',
  RESEND_INVITES: 'invites/resend',
  RECLASSIFY_PURPOSES: 'invites/reclassify_purposes',
  BULK_INVITE: 'invites/bulk',
  BULK_INVITE_TEMPLATE: 'invites/bulk/new',
  ACCEPT_INVITE: (inviteId: ID) => {
    return `invites/${inviteId}/accept`
  }
}

export const SUBSCRIPTIONS_ENDPOINTS = {
  BASE: 'subscriptions',
  CANCEL_SUBSCRIPTION: 'subscriptions/cancel_subscription',
  PLANS: 'subscriptions/me/plans',
  CHECK_COUPON: 'subscriptions/check_coupon',
  UPDATE_CUSTOMER: 'subscriptions/update_customer',
  CURRENT_SUBSCRIPTION: 'subscriptions/current',
  INVOICES: 'subscriptions/current/invoices',
  REACTIVATE_SUBSCRIPTION: 'subscriptions/reactivate_subscription',
  STRIPE_SESSIONS: 'stripe_sessions'
}

export const DATA_EXPORTS_ENDPOINTS = {
  BASE: 'data_exports',
  /** Tax report */
  TAX_REPORT: 'tax_report',
  TEAM_TAX_REPORT: 'team_tax_report'
}

export const REPORTS_ENDPOINTS = {
  BASE: 'reports',
  TEAM_REPORTS_BATCH_DOWNLOAD: 'team_reports/download',
  REPORT_PAY_PERIODS: 'report_pay_periods',
  TEAM_REPORTS: (teamId: ID) => {
    return `teams/${teamId}/reports`
  },
  REPORT: (reportId: ID) => {
    return `reports/${reportId}`
  },
  REPORT_STATUS: (reportId: ID) => {
    return `reports/${reportId}/status`
  },
  REPORT_EVENTS: (reportId: ID) => {
    return `reports/${reportId}/report_events`
  },
  REPORT_COMMENT: (reportId: ID) => {
    return `reports/${reportId}/comments`
  },
  REPORT_GUEST_COMMENT: (reportId: ID) => {
    return `reports/${reportId}/guest_comments`
  },
  REPORT_DOWNLOAD: (reportId: ID) => {
    return `reports/${reportId}/download`
  },
  REPORT_SHARE: (reportId: ID) => {
    return `reports/${reportId}/share`
  },
  REPORT_REMOVE_ITEMS: (reportId: ID) => {
    return `reports/${reportId}/items/remove`
  },
  TEAM_REPORT: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}`
  },
  TEAM_REPORT_STATUS: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/status`
  },
  TEAM_REPORT_EVENTS: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/report_events`
  },
  TEAM_REPORT_COMMENT: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/comments`
  },
  TEAM_REPORT_GUEST_COMMENT: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/guest_comments`
  },
  TEAM_REPORT_DOWNLOAD: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/download`
  },
  TEAM_REPORT_SHARE: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/share`
  },
  DETACH_FROM_REPORT: (
    reportId: ID,
    kind: 'trips' | 'transactions',
    id: TripShort['token_id'] | TransactionShort['token_id']
  ) => {
    return `reports/${reportId}/${kind}/${id}`
  },
  TEAM_REPORT_BATCH_STATUS_UPDATE: (teamId: ID) => {
    return `teams/${teamId}/report_batches/status`
  },
  TEAM_REPORT_REJECT_ITEMS: (teamId: ID, reportId: ID) => {
    return `teams/${teamId}/reports/${reportId}/items/reject`
  },
  TEAM_REPORT_BATCH_MOVE_PAY_PERIOD: (teamId: ID) => {
    return `teams/${teamId}/report_batches/move_to_pay_period`
  },
  TEAM_REPORT_BULK_UPDATE: (teamId: ID) => {
    return `/teams/${teamId}/report_batches/bulk`
  },
  TEAM_REPORT_BULK_UPDATE_TEMPLATE: (teamId: ID) => {
    return `teams/${teamId}/report_batches/bulk/new`
  },
  TEAM_REPORTS_SUMMARY: (teamId: ID) => {
    return `teams/${teamId}/reports/summary`
  },
  TAX_REPORT_NOTIFICATION_ACK: (taxReportId: ID) => {
    return `tax-reports/${taxReportId}/acknowledgements`
  },
  TAX_REPORT_NOTIFICATIONS: 'tax-reports',
  ADDITIONAL_DISTANCE: (reportId: ID) =>
    `reports/${reportId}/additional-distance`
}

export const COMPLIANCE_ENDPOINTS = {
  COMPLIANCE_SUBMISSIONS: 'compliance/submission',
  COMPLIANCE_WAITLIST_ENTRIES: 'waitlist_entries',
  VIN_LOOKUP: 'vin_lookup',
  TEAM_COMPLIANCE_SUBMISSIONS: (teamId: ID) => {
    return `teams/${teamId}/team_compliance/submissions`
  },
  TEAM_COMPLIANCE_SUBMISSION: (teamId: ID, complianceId: ID) => {
    return `teams/${teamId}/team_compliance/submissions/${complianceId}`
  },
  TEAM_COMPLIANCE_SUBMISSION_REVIEW: (teamId: ID, complianceId: ID) => {
    return `teams/${teamId}/team_compliance/submit_review/${complianceId}`
  },
  TEAM_COMPLIANCE_SUBMISSION_START_REVIEW: (teamId: ID, complianceId: ID) => {
    return `teams/${teamId}/team_compliance/start_review_submission/${complianceId}`
  }
}

export const TRIPS_ENDPOINTS = {
  BASE: 'trips',
  MERGE_TRIPS: 'merge_trips',
  BATCH_DELETE_TRIPS: 'trip_batches/delete',
  BATCH_UPDATE_TRIPS: 'trip_batches/update',
  /** Favorite trips */
  FAVORITE_TRIPS: 'favorite_trips',
  LOCKED_SUMMARY: 'trips/locked_summary',
  EXPORT: 'trips/export',
  MERGE_TRIP_BY_ID: (tripId: ID) => {
    return `merge_trips/${tripId}`
  },
  TRIP: (tripId: ID) => {
    return `trips/${tripId}`
  },
  REPORT_TRIP: (teamId: ID, tripId: ID) => {
    return `teams/${teamId}/trips/${tripId}`
  },
  FAVORITE_TRIP: (tripId: ID) => {
    return `/favorite_trips/${tripId}`
  },
  CONNECT_TRIP: (tripId: ID) => {
    return `trips/${tripId}/reconnect_stops`
  },
  CLASSIFICATION: 'trips/classification'
}

export const TRANSACTIONS_ENDPOINTS = {
  BASE: 'transactions',
  BATCH_DELETE_TRANSACTIONS: 'transaction_batches/delete',
  BATCH_UPDATE_TRANSACTIONS: 'transaction_batches/update',
  TRANSACTION: (transactionId: ID) => {
    return `transactions/${transactionId}`
  },
  TEAM_TRANSACTION: (teamId: ID, transactionId: ID) => {
    return `/teams/${teamId}/transactions/${transactionId}`
  }
}

export const SCHEDULES_ENDPOINTS = {
  BASE: 'pay_schedules',
  NEW_SCHEDULE: 'pay_schedules/new',
  PAY_SCHEDULE: (scheduleId: ID) => {
    return `pay_schedules/${scheduleId}`
  }
}

export const PAY_PERIODS_ENDPOINTS = {
  BASE: 'pay_periods',
  PAY_PERIODS_DOWNLOAD: 'pay_periods/download',
  PAY_PERIOD_FUNDING_DOWNLOAD: 'pay_periods/funding_download',
  ASSISTANT: 'assistants/pay_period',
  PAY_PERIOD: (payPeriodId: ID) => {
    return `pay_periods/${payPeriodId}`
  },
  PAY_PERIOD_STATUS: (payPeriodId: ID) => {
    return `pay_periods/${payPeriodId}/status`
  },
  PAY_PERIOD_DOWNLOAD: (payPeriodId: ID) => {
    return `pay_periods/${payPeriodId}/download`
  },
  REIMBURSEMENTS_CHARTS: (teamId: ID) => {
    return `teams/${teamId}/charts/reimbursements`
  }
}

export const TAGS_ENDPOINTS = {
  BASE: 'tags',
  BATCH_TAGS: 'tags/batch_tags',
  BATCH_TAGS_TEMPLATE: 'tags/batch_tags/new',
  TAG: (tagId: ID) => {
    return `tags/${tagId}`
  },
  /** Categories */
  BATCH_CATEGORIES_TEMPLATE: 'expense_categories/batch_expense_categories/new',
  BATCH_CATEGORIES: 'expense_categories/batch_expense_categories',
  TEAM_CATEGORIES: (teamId: ID) => {
    return `teams/${teamId}/expense_categories`
  },
  CATEGORY: (teamId: ID, categoryId: ID) => {
    return `teams/${teamId}/expense_categories/${categoryId}`
  }
}

export const PLACES_ENDPOINTS = {
  BASE: 'places',
  TEAM_PLACES: (teamId: ID) => {
    return `teams/${teamId}/places`
  },
  PLACE: (placeId: ID) => {
    return `places/${placeId}`
  },
  TEAM_PLACE: (teamId: ID, placeId: ID) => {
    return `teams/${teamId}/places/${placeId}`
  },
  ACTIVATE_TEAM_PLACE: (teamId: ID, placeId: ID) => {
    return `teams/${teamId}/places/${placeId}/activate`
  },
  IMPORT_TEAM_PLACES: (teamId: ID) => {
    return `teams/${teamId}/places/import`
  }
}

export const UNIT_ENDPOINTS = {
  BASE: 'application_forms',
  ACTIVE_UNIT_CUSTOMER: 'users/me/unit_customer',
  UNIT_ACCOUNTS: 'unit_accounts',
  COUNTER_PARTY_ACCOUNTS: 'unit_counterparties',
  COUNTER_PARTY_ACCOUNT: (tokenId: ID) => {
    return `unit_counterparties/${tokenId}`
  },
  ACTIVE_APPLICATION_FORM: (teamId: ID) => {
    return `teams/${teamId}/application_forms/active`
  },
  TEAM_UNIT_CUSTOMER: (teamId: ID) => {
    return `teams/${teamId}/unit_customer`
  }
}

export const RULES_ENDPOINTS = {
  BASE: 'automation/rules',
  RULES_INTROSPECTION: 'automation/rules/introspection',
  RULE: (ruleId: ID) => {
    return `automation/rules/${ruleId}`
  }
}

export const INTEGRATIONS_ENDPOINTS = {
  MERGE_INTEGRATIONS_METADATA: 'group_integrations/metadata',
  MERGE_INTEGRATIONS: (teamId: ID) => {
    return `teams/${teamId}/group_integrations`
  },
  MERGE_INTEGRATION: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}`
  },
  MERGE_INTEGRATION_DROPDOWN_FILTERS: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}/dropdown_filters`
  },
  MERGE_INTEGRATION_FILTERS: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}/filters`
  },
  MERGE_INTEGRATION_MEMBERS: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}/employees`
  },
  MERGE_INTEGRATION_PREVIEW_MEMBERS: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}/preview_employees`
  },
  MERGE_INTEGRATION_REMOTE_FIELDS: (teamId: ID, integrationId: ID) => {
    return `teams/${teamId}/group_integrations/${integrationId}/remote_fields`
  },
  MERGE_LINK_TOKEN: (teamId: ID) => {
    return `teams/${teamId}/group_integrations/link_token`
  },
  MERGE_RELINK_TOKEN: (teamId: ID, groupIntegrationId: ID) => {
    return `teams/${teamId}/group_integrations/${groupIntegrationId}/relink`
  },
  MERGE_PREVIEW_MANAGERS: (teamId: ID, groupIntegrationId: ID) => {
    return `teams/${teamId}/group_integrations/${groupIntegrationId}/preview_managers`
  },
  MERGE_DOWNLOAD_MANAGERS: (teamId: ID, groupIntegrationId: ID) => {
    return `teams/${teamId}/group_integrations/${groupIntegrationId}/download_managers`
  },
  MERGE_SWAP: (teamId: ID) => {
    return `teams/${teamId}/group_integrations/swap`
  },
  MERGE_FIELDS_TO_SYNC: (teamId: ID, groupIntegrationId: ID) => {
    return `teams/${teamId}/group_integrations/${groupIntegrationId}/fields_to_sync`
  }
}

export const SALES_ENDPOINTS = {
  BASE: 'sales',
  DATA_CLEANING: 'sales/data_cleaning'
}

export const CATALOG_ENDPOINTS = {
  BASE: 'catalog',
  COUNTRIES: 'catalog/countries',
  STATES: 'catalog/states'
}
